import { create } from 'zustand';
import { cookieUtils } from '../utils/CookieUtils';
import { tokenUtils } from '../utils/TokenUtils';

// 사용자 정보 초기상태
const userInfo = {
    userName: null,
    userEmail: null,
    isAdmin: false,
    currentOrganizationCode: null,
    currentOrganizationName: null,
    currentRole: null,
    roles: [],
    orgUserList: [],
    currentOrganizationApproved: false
}

const refreshTokenUtils = {
    get: () => {
        return cookieUtils.getCookie('refreshToken');
    },
    
    set: (token, days = 14) => {
        // 프로덕션 환경에서는 secure: true 설정 권장
        //const isProduction = process.env.NODE_ENV === 'production';
        
        cookieUtils.setCookie('refreshToken', token, {
            days: days,
            path: '/',
            sameSite: 'Lax', // ← Strict에서 Lax로 변경
            secure: false,
            //sameSite: 'Strict', // CSRF 공격 방지
            //secure: isProduction, // HTTPS에서만 전송 (프로덕션에서만)
            // httpOnly는 클라이언트에서 접근해야 하므로 false
        });
    },
    
    remove: () => {
        cookieUtils.deleteCookie('refreshToken');
    },
    
    exists: () => {
        return !!refreshTokenUtils.get();
    },
    
    // RefreshToken 만료 시간 확인
    getExpireInfo: () => {
        const token = refreshTokenUtils.get();
        if (!token) return null;
        
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expireTime = payload.exp ? new Date(payload.exp * 1000) : null;
            const isExpired = expireTime ? Date.now() >= expireTime.getTime() : true;
            
            return {
                expireTime,
                isExpired,
                timeUntilExpiry: expireTime ? Math.max(0, expireTime.getTime() - Date.now()) : 0
            };
        } catch (error) {
            console.error('RefreshToken 파싱 오류:', error);
            return null;
        }
    }
};

const useTokenStore = create((set, get) => ({
    // State - 초기 상태
    accessToken: null,
    refreshToken: cookieUtils.getCookie('refreshToken'),
    user: userInfo,
    isAuthenticated: false,
    tokenExpireTime: null,
    needsTokenRefresh: false,
    orgModalOpen: false, // 로그인용 추가됨

    // Actions
    setUser: (userData) => {
        set((state) => ({
            user: { ...state.user, ...userData }
        }));
    },
    setAccessToken: (token) => {
        //console.log('setAccessToken 호출됨');
        const expireTime = tokenUtils.getTokenExpireTime(token);
        const roles = tokenUtils.parseToken(token)?.realm_access?.roles || []; // 역할 정보
        const { userEmail, userName, isAdmin, isEmailVerified } = tokenUtils.parseToken(token) || {}; // 사용자 정보

        // 모든 업데이트를 한 번에 처리하여 리렌더링 최소화
        set((state) => {
            const updatedUser = {
                ...state.user,
                userEmail,
                userName,
                isAdmin,
                isEmailVerified,
            };

            // roles 처리
            if(roles.length > 0) {
                if(roles.includes('ADMIN')) {
                    updatedUser.isAdmin = true;
                    updatedUser.roles = roles;
                    updatedUser.currentRole = roles[0];
                } else {
                    updatedUser.isAdmin = false;
                    updatedUser.roles = roles;
                }
            }

            return {
                user: updatedUser,
                accessToken: token,
                tokenExpireTime: expireTime,
                needsTokenRefresh: false
            };
        });
    },
    setRefreshToken: (token) => {
        refreshTokenUtils.set(token);
    },
    setTokens: (accessToken, refreshToken) => {
        //console.log('setTokens 호출됨');
        const { setAccessToken, setRefreshToken } = get();
        setAccessToken(accessToken);
        if (refreshToken) {
            setRefreshToken(refreshToken);
        }
    },
    setAuthenticated: (value) => {
        set({ isAuthenticated: value });
    },
    clearTokens: () => {
        // 쿠키에서 RefreshToken 제거
        refreshTokenUtils.remove();

        // AccessToken 및 사용자 정보 스토어에서 제거
        set({
            accessToken: null,
            user: null,
            isAuthenticated: false,
            tokenExpireTime: null,
            needsTokenRefresh: false,
            orgModalOpen: false
        });
    },

    // Getters
    getAccessToken: () => get().accessToken,
    
    getRefreshToken: () => {
        // RefreshToken은 쿠키에 저장되어 있으므로 쿠키에서 직접 읽어옴
        return refreshTokenUtils.get();
    },
    
    getUser: () => get().user,
    
    isTokenValid: () => {
        const { accessToken } = get();
        return accessToken && !tokenUtils.isTokenExpired(accessToken);
    },
    isRefreshTokenValid: () => {
        // RefreshToken은 쿠키에 저장되어 있으므로 쿠키에서 직접 읽어옴
        const refreshToken = refreshTokenUtils.get();
        return refreshToken && !tokenUtils.isTokenExpired(refreshToken);
    },
    hasValidTokens: () => {
        const { isTokenValid, isRefreshTokenValid } = get();
        return isTokenValid() || isRefreshTokenValid();
    },
    // 토큰 만료까지 남은 시간 (밀리초)
    getTimeUntilExpiry: () => {
        const { tokenExpireTime } = get();
        if (!tokenExpireTime) return 0;
        return Math.max(0, tokenExpireTime.getTime() - Date.now());
    },

    // 디버그용 정보
    getTokenInfo: () => {
        const { accessToken, user, tokenExpireTime } = get();
        // RefreshToken은 쿠키에서 읽어옴
        const refreshToken = refreshTokenUtils.get();
        const refreshExpireInfo = refreshTokenUtils.getExpireInfo();
        
        return {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            isAuthenticated: get().isAuthenticated,
            user,
            tokenExpireTime,
            timeUntilExpiry: get().getTimeUntilExpiry(),
            accessTokenExpired: accessToken ? tokenUtils.isTokenExpired(accessToken) : true,
            refreshTokenExpired: refreshToken ? tokenUtils.isTokenExpired(refreshToken) : true,
            refreshTokenInfo: refreshExpireInfo,
            cookieInfo: {
                refreshTokenExists: refreshTokenUtils.exists(),
                allCookies: typeof document !== 'undefined' ? document.cookie : 'N/A (SSR)'
            }
        };
    },

    setOrgModalOpen: (value) => { // ← 추가
        set({ orgModalOpen: value });
    },
}));

export default useTokenStore;

